/* eslint-disable max-len */
import { Invoice } from '@biz-nest/commerce-client';
import { FrontendLogger, IecommerceSalesAnalytics, IfilterProps } from '@biz-nest/core-client';

export type TecomSalePeriod = 'weekly' | 'monthly' | 'yearly';

export const salesAnalyticsEcommerceForSharedUse = () => {
  const getSalesByYears = async() => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const promises = months.map((month, index) =>{
      const startDate = new Date(new Date().getFullYear(), index, 1);
      const endDate = new Date(new Date().getFullYear(), index + 1, 0);

      const filter: IfilterProps = {
        comparisonFilter: [
          {
            field: 'createdAt',
            fieldValue: startDate,
            operator: 'gte'
          },
          {
            field: 'createdAt',
            fieldValue: endDate,
            operator: 'lte'
          }
        ]
      };

      return Invoice.getEcommerceSalesAnalytics(filter);
    });

    const all = await Promise.all(promises);

    return all;
  };


  const getSalesMonth = async(month: number) => {
    const daysInMonth = Array
      .from(Array(new Date(new Date().getFullYear(), month + 1, 0).getDate()).keys()).map(x => x + 1);

    const promises = daysInMonth.map(day =>{
      const startDate = new Date(new Date().getFullYear(), month, day);
      const endDate = new Date(new Date().getFullYear(), month, day + 1);

      const filter: IfilterProps = {
        comparisonFilter: [
          {
            field: 'createdAt',
            fieldValue: startDate,
            operator: 'gte'
          },
          {
            field: 'createdAt',
            fieldValue: endDate,
            operator: 'lte'
          }
        ]
      };

      return Invoice.getEcommerceSalesAnalytics(filter);
    });

    const all = await Promise.all(promises);

    return all;
  };

  const getDaysInCurrentWeek = (): Date[] => {
    const now = new Date();
    const currentDay = now.getDay(); // 0 (Sunday) to 6 (Saturday)
    const days: Date[] = [];

    // Calculate start of the week (Sunday) and generate 7 consecutive days
    for (let i = 0; i < 7; i++) {
      const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() - currentDay + i);

      days.push(day);
    }

    return days;
  };

  const getSalesForCurrentWeek = async(): Promise<IecommerceSalesAnalytics[]> => {
    const daysInWeek = getDaysInCurrentWeek();

    const promises = daysInWeek.map(day => {
      const startDate = new Date(day.getFullYear(), day.getMonth(), day.getDate());
      const endDate = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);

      const filter: IfilterProps = {
        comparisonFilter: [
          {
            field: 'createdAt',
            fieldValue: startDate,
            operator: 'gte'
          },
          {
            field: 'createdAt',
            fieldValue: endDate,
            operator: 'lte'
          }
        ]
      };

      return Invoice.getEcommerceSalesAnalytics(filter);
    });

    const weeklySalesAnalytics = await Promise.all(promises);

    return weeklySalesAnalytics;
  };

  const getAllSales = async(period: TecomSalePeriod = 'yearly') => {
    try {
      let salesAnalytics: IecommerceSalesAnalytics[] = [];

      switch (period) {
        case 'weekly':
          salesAnalytics = await getSalesForCurrentWeek();
          break;
        case 'monthly':
          salesAnalytics = await getSalesByYears();
          break;
        case 'yearly':
          salesAnalytics = await getSalesByYears();
          break;
      }

      // Safely calculate totals with fallback
      const totalSales = salesAnalytics.reduce((acc, val) => acc + (val.totalSales || 0), 0);
      const totalTax = salesAnalytics.reduce((acc, val) => acc + (val.totalSales || 0), 0);
      const totalCount = salesAnalytics.reduce((acc, val) => acc + (val.count || 0), 0);

      // Update sales data
      const salesData = {
        totalSales: totalSales,
        tax: totalTax,
        count: totalCount,
        earnings: totalSales - totalTax
      };

      // Calculate percentages with safety checks
      const baseTotal = totalSales || 1;

      const salesPercentage = (salesData.totalSales / baseTotal) * 100;
      const revenuePercentage = (salesData.earnings / baseTotal) * 100;

      // Optional: Log for debugging
      FrontendLogger.debug('Sales Analytics:', salesData);

      return {
        salesData,
        salesPercentage,
        revenuePercentage,
        salesAnalytics
      };
    } catch (error) {
      FrontendLogger.error('Error fetching sales totals', error);

      return resetSalesData();
    }
  };

  const resetSalesData = () => {
    return {
      salesData: {
        totalSales: 0,
        tax: 0,
        count: 0,
        earnings: 0
      },
      salesPercentage: 0,
      revenuePercentage: 0,
      salesAnalytics: []
    };
  };

  return {
    getSalesByYears,
    getSalesMonth,
    getDaysInCurrentWeek,
    getSalesForCurrentWeek,
    getAllSales,
    resetSalesData
  };
};
